import React, { useState } from 'react';
import { ApiClient } from '../../api/client';
import posthog from 'posthog-js';
import { useTranslation } from 'react-i18next';

export interface SendToTelegramButtonProps {
  creationId: string;
  resultFileId: string;
  momentTitle: string;
  /**
   * Whether the creation is already shared publicly. If undefined,
   * the component will fetch the creation details before sending.
   */
  isShared?: boolean;
  /**
   * The strategy type for analytics tracking
   */
  strategyType?: string;
}

// Function to escape special characters for Telegram's MarkdownV2 format
const escapeMarkdownV2 = (text: string): string => {
  // These characters need to be escaped in MarkdownV2: '_', '*', '[', ']', '(', ')', '~', '`', '>', '#', '+', '-', '=', '|', '{', '}', '.', '!'
  return text.replace(/[_*[\]()~`>#+=\-|{}.!]/g, '\\$&');
};

export function SendToTelegramButton({ creationId, resultFileId, momentTitle, isShared, strategyType }: SendToTelegramButtonProps) {
  const [isSending, setIsSending] = useState(false);
  const { t } = useTranslation();

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isSending) return;
    setIsSending(true);
    posthog.capture('send_to_telegram_clicked', { 
      creation_id: creationId, 
      moment_title: momentTitle,
      strategy_type: strategyType
    });
    try {
      // Ensure the creation is publicly accessible so others can view it
      let shared = isShared;
      if (shared === undefined) {
        try {
          const creation = await ApiClient.getCreation(creationId);
          shared = creation.is_shared ?? false;
        } catch (err) {
          console.warn('Failed to fetch creation info:', err);
          // If we cannot determine, assume private and continue
          shared = false;
        }
      }
      if (!shared) {
        try {
          await ApiClient.makeCreationPublic(creationId);
        } catch (err) {
          console.warn('Failed to make creation public before sending:', err);
          // continue sending even if making public fails
        }
      }

      const botUsername = import.meta.env.VITE_BOT_USERNAME;
      const miniAppName = import.meta.env.VITE_MINI_APP_NAME;
      const link = `https://t.me/${botUsername}/${miniAppName}?startapp=creation_${creationId}`;
      
      // Escape the moment title for MarkdownV2
      const escapedTitle = escapeMarkdownV2(momentTitle);
      const description = t('sendToTelegramButton.sentMessageDescription', { title: escapedTitle });
      
      await ApiClient.sendCreationToTelegram(creationId, {
        result_file_id: resultFileId,
        description,
        button_text: t('sendToTelegramButton.sentMessageButtonText'),
        button_url: link
      });
      alert(t('sendToTelegramButton.successAlert'));
    } catch (error) {
      console.error('Error sending to Telegram:', error);
      alert(t('sendToTelegramButton.errorAlert'));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={isSending}
      className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-all duration-150 ease-in-out"
    >
      <span className="material-symbols-outlined mr-1">send</span>
      <span className="font-medium">{isSending ? t('sendToTelegramButton.sendingButtonText') : t('sendToTelegramButton.idleButtonText')}</span>
    </button>
  );
} 